import { useCallback, useRef, useState, useLayoutEffect } from "react"

// ResizeObserverEntry.contentRect は padding を含まないため、
// safe-area分のpadding-topを含めた実際の高さ(border-box)を取得する。
// borderBoxSize未対応の環境ではgetBoundingClientRect()にフォールバックする
// (contentRectはpaddingを含まず過小計測になるため使わない)。
const getBorderBoxHeight = (entry: ResizeObserverEntry): number =>
    entry.borderBoxSize?.[0]?.blockSize ??
    entry.target.getBoundingClientRect().height

interface LayoutElement<T extends Element = Element> {
    ref: (node: T | null) => void
    style: React.CSSProperties
}

interface UseMobileLayoutPropsReturn<
    HeaderElement extends Element,
    FooterElement extends Element
> {
    rootProps: {
        style: React.CSSProperties
    }
    headerProps: LayoutElement<HeaderElement>
    footerProps: LayoutElement<FooterElement>
    bodyProps: {
        style: {
            paddingTop: number
            paddingBottom: number
        }
    }
}

export const useMobileLayoutProps = <
    HeaderElement extends Element = HTMLElement,
    FooterElement extends Element = HTMLElement
>(): UseMobileLayoutPropsReturn<HeaderElement, FooterElement> => {
    useLayoutEffect(() => {
        document.body.style.cssText = `
            height: 100%;
            height: -moz-available;
            height: -webkit-fill-available;  
            height: fill-available;
        `
    }, [])

    const [headerHeight, setHeaderHeight] = useState(0)
    const [footerHeight, setFooterHeight] = useState(0)
    const headerRef = useRef<HeaderElement | null>(null)
    const footerRef = useRef<FooterElement | null>(null)
    const headerObserverRef = useRef<ResizeObserver | null>(null)
    const footerObserverRef = useRef<ResizeObserver | null>(null)

    const headerRefCallback = useCallback((node: HeaderElement | null) => {
        if (headerObserverRef.current) {
            headerObserverRef.current.disconnect()
            headerObserverRef.current = null
        }

        headerRef.current = node

        if (node) {
            const observer = new ResizeObserver(entries => {
                if (!entries.length) return
                setHeaderHeight(getBorderBoxHeight(entries[0]))
            })
            observer.observe(node, { box: "border-box" })
            headerObserverRef.current = observer
        }
    }, [])

    const footerRefCallback = useCallback((node: FooterElement | null) => {
        if (footerObserverRef.current) {
            footerObserverRef.current.disconnect()
            footerObserverRef.current = null
        }

        footerRef.current = node

        if (node) {
            const observer = new ResizeObserver(entries => {
                if (!entries.length) return
                setFooterHeight(getBorderBoxHeight(entries[0]))
            })
            observer.observe(node, { box: "border-box" })
            footerObserverRef.current = observer
        }
    }, [])

    return {
        rootProps: {
            style: {
                position: "absolute",
                height: "100%",
                width: "100%",
                top: 0,
                left: 0
            }
        },
        headerProps: {
            ref: headerRefCallback,
            style: {
                position: "fixed",
                width: "100%",
                zIndex: 99,
                top: 0,
                boxSizing: "border-box",
                paddingTop: "env(safe-area-inset-top, 0px)"
            }
        },
        footerProps: {
            ref: footerRefCallback,
            style: {
                position: "fixed",
                width: "100%",
                zIndex: 99,
                bottom: 0
            }
        },
        bodyProps: {
            style: {
                paddingTop: headerHeight,
                paddingBottom: footerHeight
            }
        }
    }
}
